import { useEffect, useState, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

const FetchUserData = ({ setLoading = true, setShowError = false }) => {
    const [userData, setUserData] = useState(null);
    const createdCwApi = useRef(null);
    const siteUrl = "*";

    // Function to refresh the token
    const refreshToken = useCallback(() => {
        console.log('SideKick - Refreshing token');
        return new Promise((resolve, reject) => {
            if (createdCwApi.current) {
                createdCwApi.current.post({ "request": "refreshssoaccess", "args": {} }, (data) => {
                    if (data && data.ssoAccessToken) {
                        console.log('SideKick - Token refreshed: ', data.ssoAccessToken);
                        const isTokenStringified = data.ssoAccessToken.startsWith('"') && data.ssoAccessToken.endsWith('"');
                        resolve(isTokenStringified ? JSON.parse(data.ssoAccessToken) : data.ssoAccessToken); // Resolve the promise with the new token
                    } else {
                        console.error('SideKick - Failed to refresh token');
                        reject(new Error('SideKick - Failed to refresh token')); // Reject the promise with an error
                    }
                });
            } else {
                reject(new Error('SideKick - createdCwApi is not defined')); // Reject the promise if createdCwApi is not defined
            }
        });
    }, []);

    const onLoadCallback = useCallback(() => {
        console.log('SideKick - onLoad fired');
        function fetchData() {
            console.log('SideKick - inside fetchData');
            const maxRetryCount = 3; // Number of retry attempts
            const retryDelay = 1000; // Delay in milliseconds
            let retryCount = 0;

            createdCwApi.current.post({ "request": "getMemberAuthentication" },
                (data) => {
                    if (data) {
                        console.log('SideKick - Data fetched successfully:', data);
                        const updatedUserData = {
                            siteId: data.site,
                            companyId: data.companyid,
                            memberId: data.memberid,
                            memberHash: data.memberHash,
                            memberContext: data.memberContext,
                        }
                        if (data.ssoAccessToken) {
                            updatedUserData.ssoAccessToken = data.ssoAccessToken;
                            // Check if token has expired and refresh if necessary
                            if (isTokenExpired(data.ssoAccessToken)) {
                                console.log('SideKick - Token expired: ', data.ssoAccessToken);
                                refreshToken()
                                    .then(newToken => {
                                        console.log('SideKick - New sso token:', newToken);
                                        updatedUserData.ssoAccessToken = newToken; // Update with the new token
                                        setUserData(updatedUserData); // Set user data with the updated token
                                    })
                                    .catch(error => {
                                        console.error('SideKick - Failed to refresh token:', error);
                                        setUserData(updatedUserData); // Set user data with the existing expired token
                                    });
                            } else {
                                setUserData(updatedUserData); // Set user data if token is not expired
                            }
                        } else {
                            setUserData(updatedUserData); // Set user data without token since no token is present
                        }
                        data.codeBase && (updatedUserData.codeBase = data.codeBase);
                        setLoading(false);
                    } else {
                        if (retryCount < maxRetryCount) {
                            console.log('SideKick - Failed to fetch user data.  Retrying ' + retryCount + ' of ' + maxRetryCount + ' attempts.');
                            retryCount++;
                            setTimeout(fetchData, retryDelay); // Retry with delay
                        } else {
                            setLoading(false);
                            console.log('Loading set to false');
                            setShowError(true);
                        }
                    }
                }
            ); //end .post
        }

        fetchData(); // Initial data fetch
    }, [refreshToken, setLoading, setShowError]);

    useEffect(() => {
        setLoading(true);
        console.log('SideKick - Loading Hosted API');
        const api = new window.ConnectWiseHostedAPI(siteUrl, {
            "eventHandlers": [
                { "event": "onLoad", "callback": onLoadCallback }
            ]
        }, false);
        createdCwApi.current = api;
        setTimeout(onLoadCallback, 1000);  // Delay to allow for api to be created
        console.log('SideKick - Hosted API loaded');

        return () => {
            console.log('SideKick - Unloading Hosted API');
        }
    }, [siteUrl, setLoading, setShowError, onLoadCallback]);

    // Function to check if the token has expired
    function isTokenExpired(token) {
        try {
            const decodedToken = jwtDecode(token);
            if (!decodedToken || !decodedToken.exp) {
                return true; // If the token is invalid or doesn't have an exp claim, consider it expired
            }
            const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
            return decodedToken.exp < currentTime;
        } catch (error) {
            console.error('Error decoding token:', error);
            return true; // If there's an error decoding the token, consider it expired
        }
    }

    return { userData, createdCwApi: createdCwApi.current };
};

export default FetchUserData;